import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './SearchBar.css';

// Simple debounce function
function debounce(func, wait) {
  let timeout;
  return function(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const SearchBar = ({ initialQuery = '', onSearch }) => {
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // Debounced fetch function
  const fetchSuggestions = useCallback(
    debounce(async (searchTerm) => {
      if (searchTerm.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);

      try {
        const response = await fetch(`/api/suggestions?q=${encodeURIComponent(searchTerm)}`);
        const data = await response.json();
        setSuggestions(data);
        setShowDropdown(data.length > 0);
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  // Initialize with initialQuery if provided
  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
    }
  }, [initialQuery]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    fetchSuggestions(value);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else {
          handleSearch();
        }
        break;
      
      case 'Escape':
        setShowDropdown(false);
        break;
        
      default:
        break;
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Navigate based on type
    if (suggestion.type === 'product') {
      if (suggestion.store_url_name) {
        navigate(`/${suggestion.city?.toLowerCase() || 'kelowna'}/${suggestion.store_url_name}/product/${suggestion.id}`);
      } else {
        handleSearch(suggestion.name);
      }
    } else if (suggestion.type === 'store') {
      // For stores, we have the url_name in the suggestion
      navigate(`/${suggestion.city?.toLowerCase() || 'kelowna'}/${suggestion.url_name}`);
    } else if (suggestion.type === 'recent') {
      setQuery(suggestion.name);
      handleSearch(suggestion.name);
    }
    
    setShowDropdown(false);
  };

  const handleSearch = (searchTerm = query) => {
    const trimmedSearch = searchTerm.trim();

    if (trimmedSearch) {
      // If onSearch is provided, call it, otherwise navigate
      if (onSearch && typeof onSearch === 'function') {
        onSearch(trimmedSearch);
      } else {
        const params = new URLSearchParams({
          q: trimmedSearch,
          page: '1',
        });

        navigate(`/search-results?${params.toString()}`);
      }
      setShowDropdown(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Group suggestions by type
  const productSuggestions = suggestions.filter(s => s.type === 'product');
  const storeSuggestions = suggestions.filter(s => s.type === 'store');
  const recentSuggestions = suggestions.filter(s => s.type === 'recent');

  return (
    <div className="search-wrapper" ref={searchRef}>
      <div className="search-bar">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder="Search for products, stores..."
          className="search-bar-input"
        />
        <button 
          type="button"
          onClick={() => handleSearch()} 
          className="search-bar-button"
          aria-label="Search"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="search-bar-button-label">Search</span>
        </button>
      </div>

      {showDropdown && (
        <div className="suggestions-dropdown">
          {isLoading && (
            <div className="suggestion-loading">
              <div className="search-loading-spinner"></div>
              <span>Loading suggestions...</span>
            </div>
          )}
          
          {!isLoading && suggestions.length > 0 && (
            <>
              {/* Products Section */}
              {productSuggestions.length > 0 && (
                <div className="suggestion-section">
                  <div className="section-header">Products</div>
                  {productSuggestions.map((suggestion, index) => {
                    const actualIndex = suggestions.findIndex(s => s === suggestion);
                    return (
                      <div
                        key={`product-${suggestion.id}`}
                        className={`suggestion-item ${actualIndex === selectedIndex ? 'selected' : ''}`}
                        onClick={() => handleSuggestionClick(suggestion)}
                        onMouseEnter={() => setSelectedIndex(actualIndex)}
                      >
                        <div className="suggestion-icon">
                          {suggestion.item_type === 'service' ? '🔧' : 
                           suggestion.item_type === 'experience' ? '🎭' : '📦'}
                        </div>
                        <div className="suggestion-text">
                          <div className="suggestion-name">{suggestion.name}</div>
                          {suggestion.price && (
                            <div className="suggestion-price">${parseFloat(suggestion.price).toFixed(2)}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Stores Section */}
              {storeSuggestions.length > 0 && (
                <div className="suggestion-section">
                  <div className="section-header">Stores</div>
                  {storeSuggestions.map((suggestion, index) => {
                    const actualIndex = suggestions.findIndex(s => s === suggestion);
                    return (
                      <div
                        key={`store-${suggestion.id}`}
                        className={`suggestion-item ${actualIndex === selectedIndex ? 'selected' : ''}`}
                        onClick={() => handleSuggestionClick(suggestion)}
                        onMouseEnter={() => setSelectedIndex(actualIndex)}
                      >
                        <div className="suggestion-icon">🏪</div>
                        <div className="suggestion-text">
                          <div className="suggestion-name">{suggestion.name}</div>
                          <div className="suggestion-type">
                            {suggestion.city ? `Store in ${suggestion.city}` : 'Store'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Recent Searches Section */}
              {recentSuggestions.length > 0 && (
                <div className="suggestion-section">
                  <div className="section-header">Recent Searches</div>
                  {recentSuggestions.map((suggestion, index) => {
                    const actualIndex = suggestions.findIndex(s => s === suggestion);
                    return (
                      <div
                        key={`recent-${index}`}
                        className={`suggestion-item ${actualIndex === selectedIndex ? 'selected' : ''}`}
                        onClick={() => handleSuggestionClick(suggestion)}
                        onMouseEnter={() => setSelectedIndex(actualIndex)}
                      >
                        <div className="suggestion-icon">🕐</div>
                        <div className="suggestion-text">
                          <div className="suggestion-name">{suggestion.name}</div>
                          <div className="suggestion-type">Recent search</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {!isLoading && suggestions.length === 0 && query.length >= 2 && (
            <div className="no-suggestions">
              No suggestions found for "{query}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
